import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { CheckCurrencyMismatchRequestDto } from "../models/common/checkCurrencyMismatchRequestDto";
import { CheckCurrencyMismatchResponseDto } from "../models/common/checkCurrencyMismatchResponseDto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { OrderNotes } from "../models/invoice-review/orderNotes";
import { CreateSalesOrderLineResponseDto } from "../models/orders/createSalesOrderLineResponseDto";
import { DeleteSOLinesResponseDto } from "../models/orders/deleteSOLinesResponseDto";
import { GetSaleOrderDtoWIthType } from "../models/orders/getSaleOrderDtoWIthType";
import { InvoiceEmail } from "../models/orders/invoicedEmail";
import { SalesOrderLine } from "../models/orders/saleOrderLine";
import { ConvertToSaleOrderDto } from "../models/qoutes/convertToSaleOrderDto";
import { CopyQuotesRequestDto } from "../models/qoutes/copyQuotesRequestDto";
import { CreateQuotesRequestDto } from "../models/qoutes/createQuotesRequestDto";
import { CreateSalesOrderHeaderResponseDto } from "../models/qoutes/createSalesOrderHeaderResponseDto";
import { GetQuotesDto } from "../models/qoutes/getQuotesDto";
import { QuotesInvoiceEmail } from "../models/qoutes/quotesInvoiceEmail";
import { QuotesLineRequestDto } from "../models/qoutes/quotesLineRequestDto";
import { QuotesResponseDto } from "../models/qoutes/quotesResponseDto";
import { UpdateDateRequestDto } from "../models/qoutes/updateDateRequestDto";
import { UpdateQuotesHeaderDto } from "../models/qoutes/updateQuotesHeaderDto";
import { UpdateQuotesLineDto } from "../models/qoutes/updateQuotesLineDto";
import { UpdateTrackableOnQuotesOrderLinesRequestDto } from "../models/qoutes/updateTrackableOnQuotesOrderLinesRequestDto";
import { CopyLinesQuotesDto } from "../models/qoutes/copyLinesQuotesDto";
import { ClientSearchList } from "../models/shared/clientSearchList";

@Injectable({
  providedIn: "root",
})
export class CreateHeaderQuoteService {
  private quoteManagementBaseURL = environment.baseUrl + "/QuotesManagement";
  private clientManagementBaseURL = environment.baseUrl + "/ClientManagement";
  constructor(private _http: HttpClient) {}

  addQuoteHeaderData(
    payload: CreateQuotesRequestDto
  ): Observable<GenericResponseDto<CreateQuotesRequestDto>> {
    return this._http.post<GenericResponseDto<CreateQuotesRequestDto>>(
      this.quoteManagementBaseURL + "/AddQuotesRequest",
      payload
    );
  }

  updateQuoteHeaderData(
    payload: UpdateQuotesHeaderDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.quoteManagementBaseURL + "/UpdateQuotesHeader",
      payload
    );
  }

  createQuoteLine(
    payload: QuotesLineRequestDto
  ): Observable<GenericResponseDto<SalesOrderLine[]>> {
    return this._http.post<GenericResponseDto<SalesOrderLine[]>>(
      this.quoteManagementBaseURL + "/SaveQuoteLines",
      payload
    );
  }

  updateQuoteLine(
    payload: UpdateQuotesLineDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.quoteManagementBaseURL + "/UpdateQuotesLine",
      payload
    );
  }

  getQuotesData(id: string): Observable<GenericResponseDto<QuotesResponseDto>> {
    return this._http.get<GenericResponseDto<QuotesResponseDto>>(
      this.quoteManagementBaseURL + "/GetQuotes?saleOrderId=" + id
    );
  }

  getQuotesEmails(
    salesOrderId: string
  ): Observable<GenericResponseDto<QuotesInvoiceEmail[]>> {
    return this._http.get<GenericResponseDto<QuotesInvoiceEmail[]>>(
      this.quoteManagementBaseURL +
        "/GetQuotesEmails?saleOrderId=" +
        salesOrderId
    );
  }

  getAllQuotes(): Observable<GenericResponseDto<GetQuotesDto[]>> {
    return this._http.get<GenericResponseDto<GetQuotesDto[]>>(
      this.quoteManagementBaseURL + "/GetQuotesInformation"
    );
  }

  getSaleOrderLineNumber(
    saleOrderId: string
  ): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.quoteManagementBaseURL +
        `/GetQuotesLineNumber?SaleOrderId=${saleOrderId}`
    );
  }

  getPartyNumberByAccountNumber(
    accountNumber: string
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.clientManagementBaseURL +
        `/GetPartyNumberByAccountNumber?accountNumber=${accountNumber}`
    );
  }

  convertQuotesToSaleOrder(
    payload: ConvertToSaleOrderDto
  ): Observable<GenericResponseDto<CreateSalesOrderHeaderResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderHeaderResponseDto>
    >(this.quoteManagementBaseURL + "/ConvertQuotesToSaleOrder", payload);
  }

  deleteLineItems(
    payload: number[]
  ): Observable<GenericResponseDto<DeleteSOLinesResponseDto>> {
    return this._http.post<GenericResponseDto<DeleteSOLinesResponseDto>>(
      this.quoteManagementBaseURL + "/DeleteQuotesLine",
      payload
    );
  }

  addHeaderData(
    payload: CopyQuotesRequestDto
  ): Observable<GenericResponseDto<CreateQuotesRequestDto>> {
    return this._http.post<GenericResponseDto<CreateQuotesRequestDto>>(
      this.quoteManagementBaseURL + "/CopyQuotesHeader",
      payload
    );
  }

  copyHeaderData(quotesId: string): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.quoteManagementBaseURL + `/CopyQuotes?QuotesId=${quotesId}`
    );
  }

  addLineData(
    payload: CopyLinesQuotesDto
  ): Observable<GenericResponseDto<CreateSalesOrderLineResponseDto>> {
    return this._http.post<GenericResponseDto<CreateSalesOrderLineResponseDto>>(
      this.quoteManagementBaseURL + "/CopyLines",
      payload
    );
  }

  updateInvoiceEmailSaleOrderHeaderData(
    payload: InvoiceEmail[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.quoteManagementBaseURL + "/AddOrUpdateInvoicedEmails",
      payload
    );
  }

  deleteInvoiceEmailSaleOrderHeaderData(
    payload: number[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.quoteManagementBaseURL + "/DeleteInvoicedEmails",
      payload
    );
  }

  acceptedRejectedAndCancelDate(
    payload: UpdateDateRequestDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.quoteManagementBaseURL + "/UpdateDateRequest",
      payload
    );
  }

  getCustomerByNameAndAccountNumberFilter(
    customerAccountNumber = "",
    salesOrderIdD365 = ""
  ): Observable<GenericResponseDto<ClientSearchList[]>> {
    return this._http.get<GenericResponseDto<ClientSearchList[]>>(
      this.clientManagementBaseURL +
        `/SearchClientAndProspectByNameOrAccountNumber?nameAccounNumber=${encodeURIComponent(
          customerAccountNumber
        )}&salesOrderIdD365=${encodeURIComponent(salesOrderIdD365)}`
    );
  }

  getQuoteNotes(
    saleOrderId: string
  ): Observable<GenericResponseDto<OrderNotes[]>> {
    return this._http.get<GenericResponseDto<OrderNotes[]>>(
      this.quoteManagementBaseURL + `/getQuoteNotes?SaleOrderId=` + saleOrderId
    );
  }

  getRecommendedTax(
    quotesId = ""
  ): Observable<GenericResponseDto<number | null>> {
    return this._http.get<GenericResponseDto<number | null>>(
      this.quoteManagementBaseURL + `/CalculateTax?QuotesId=${quotesId}`
    );
  }

  getAllQuotesAndCombineItInSalesHeader(): Observable<
    GenericResponseDto<GetSaleOrderDtoWIthType[]>
  > {
    return this._http.get<GenericResponseDto<GetSaleOrderDtoWIthType[]>>(
      this.quoteManagementBaseURL + "/GetQuotesInformation"
    );
  }

  checkCurrencyMismatch(
    payload: CheckCurrencyMismatchRequestDto
  ): Observable<GenericResponseDto<CheckCurrencyMismatchResponseDto>> {
    return this._http.post<
      GenericResponseDto<CheckCurrencyMismatchResponseDto>
    >(this.quoteManagementBaseURL + "/CheckCurrencyMismatch", payload);
  }

  updateTrackableOnSalesOrderLines(
    payload: UpdateTrackableOnQuotesOrderLinesRequestDto[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.quoteManagementBaseURL + "/UpdateTrackableOnQuotesLines",
      payload
    );
  }
}
